/**
 * Sidebar announcements and the Admin › Announcements page.
 */
const en = {
  'nav.announcements': 'Announcements',

  // Sidebar card
  'announce.dismiss': 'Dismiss announcement',
  'announce.learnMore': 'Learn more',
  'announce.prev': 'Previous announcement',
  'announce.next': 'Next announcement',
  'announce.position': 'Announcement {n} of {total}',

  'announce.tone.info': 'Info',
  'announce.tone.success': 'Success',
  'announce.tone.warning': 'Important',
  'announce.tone.celebrate': 'Celebrate',

  'announce.icon.megaphone': 'Megaphone',
  'announce.icon.sparkles': 'Sparkles',
  'announce.icon.info': 'Info',
  'announce.icon.alert': 'Alert',
  'announce.icon.gift': 'Gift',
  'announce.icon.party': 'Party',
  'announce.icon.coins': 'Coins',
  'announce.icon.rocket': 'Rocket',
  'announce.icon.calendar': 'Calendar',
  'announce.icon.bell': 'Bell',
  'announce.icon.wrench': 'Maintenance',
  'announce.icon.shield': 'Security',

  // Admin page
  'announce.admin.title': 'Announcements',
  'announce.admin.subtitle': 'Publish notices that appear in everyone’s sidebar. Add a picture, an icon, a link and a schedule.',
  'announce.admin.new': 'New announcement',
  'announce.admin.loading': 'Loading announcements…',
  'announce.admin.loadFailed': 'Could not load announcements.',
  'announce.admin.none': 'No announcements yet. Create one to show it in every user’s sidebar.',
  'announce.admin.noneInFilter': 'Nothing here.',
  'announce.admin.filter': 'Filter announcements',

  'announce.status.all': 'All',
  'announce.status.live': 'Live',
  'announce.status.scheduled': 'Scheduled',
  'announce.status.ended': 'Ended',
  'announce.status.draft': 'Draft',

  'announce.admin.window': '{from} → {to}',
  'announce.admin.now': 'Now',
  'announce.admin.noEnd': 'No end date',
  'announce.admin.dismissedOne': 'Closed by 1 user',
  'announce.admin.dismissedMany': 'Closed by {n} users',
  'announce.admin.notDismissible': 'Can’t be closed',
  'announce.admin.createdBy': 'By {name}',
  'announce.admin.edit': 'Edit {title}',
  'announce.admin.duplicate': 'Duplicate {title}',
  'announce.admin.publish': 'Publish {title}',
  'announce.admin.unpublish': 'Unpublish {title}',
  'announce.admin.delete': 'Delete {title}',
  'announce.admin.copySuffix': '(copy)',

  'announce.admin.createTitle': 'New announcement',
  'announce.admin.editTitle': 'Edit announcement',
  'announce.admin.sectionContent': 'Content',
  'announce.admin.sectionLook': 'Look',
  'announce.admin.sectionLink': 'Button link',
  'announce.admin.sectionSchedule': 'Schedule',
  'announce.admin.sectionOptions': 'Options',
  'announce.admin.titleEn': 'Title (English)',
  'announce.admin.titleAr': 'Title (Arabic)',
  'announce.admin.bodyEn': 'Message (English)',
  'announce.admin.bodyAr': 'Message (Arabic)',
  'announce.admin.arOptional': 'Optional. Arabic users see the English text when this is empty.',
  'announce.admin.chars': '{n} / {max}',
  'announce.admin.icon': 'Icon',
  'announce.admin.tone': 'Style',
  'announce.admin.image': 'Picture',
  'announce.admin.imageAdd': 'Add a picture',
  'announce.admin.imageReplace': 'Replace',
  'announce.admin.imageRemove': 'Remove picture',
  'announce.admin.imageHint': 'PNG, JPEG, WebP or GIF. Wide images work best; it’s resized automatically.',
  'announce.admin.imageProcessing': 'Preparing picture…',
  'announce.admin.imageInvalid': 'That file isn’t an image we can use.',
  'announce.admin.imageTooBig': 'That picture is too large (10 MB max).',
  'announce.admin.linkUrl': 'Link',
  'announce.admin.linkUrlHint': 'https://… or a page in the app, such as /tasks. Leave empty for no button.',
  'announce.admin.linkLabel': 'Button text (English)',
  'announce.admin.linkLabelAr': 'Button text (Arabic)',
  'announce.admin.startsAt': 'Starts',
  'announce.admin.endsAt': 'Ends',
  'announce.admin.startsHint': 'Empty = straight away.',
  'announce.admin.endsHint': 'Empty = until you unpublish it.',
  'announce.admin.presetMonth': 'Until end of month',
  'announce.admin.presetWeek': 'For 1 week',
  'announce.admin.presetNone': 'No end date',
  'announce.admin.published': 'Published',
  'announce.admin.publishedHint': 'Off = saved as a draft that nobody sees.',
  'announce.admin.dismissible': 'Users can close it',
  'announce.admin.dismissibleHint': 'Turn off for notices everyone must keep seeing.',
  'announce.admin.reset': 'Show it again to everyone who closed it',
  'announce.admin.resetHint': '{n} user(s) closed this announcement.',
  'announce.admin.preview': 'Live preview',
  'announce.admin.previewHint': 'This is how it looks in the sidebar.',
  'announce.admin.previewTitle': 'Your title',
  'announce.admin.previewBody': 'Your message appears here.',
  'announce.admin.create': 'Publish announcement',
  'announce.admin.saveDraft': 'Save draft',
  'announce.admin.update': 'Save changes',
  'announce.admin.createdToast': '“{title}” is saved.',
  'announce.admin.updatedToast': '“{title}” is updated.',
  'announce.admin.deletedToast': '“{title}” is deleted.',
  'announce.admin.publishedToast': '“{title}” is published.',
  'announce.admin.unpublishedToast': '“{title}” is now a draft.',
  'announce.admin.saveFailed': 'Could not save the announcement.',
  'announce.admin.deleteFailed': 'Could not delete the announcement.',
  'announce.admin.deleteTitle': 'Delete “{title}”?',
  'announce.admin.deleteMessage': 'It disappears from every sidebar. This can’t be undone.',
  'announce.admin.deleteConfirm': 'Delete',
  'announce.admin.endBeforeStart': 'The end date must be after the start date.',
};

const ar: Record<keyof typeof en, string> = {
  'nav.announcements': 'الإعلانات',

  'announce.dismiss': 'إغلاق الإعلان',
  'announce.learnMore': 'اعرف المزيد',
  'announce.prev': 'الإعلان السابق',
  'announce.next': 'الإعلان التالي',
  'announce.position': 'الإعلان {n} من {total}',

  'announce.tone.info': 'معلومة',
  'announce.tone.success': 'نجاح',
  'announce.tone.warning': 'مهم',
  'announce.tone.celebrate': 'احتفال',

  'announce.icon.megaphone': 'مكبر صوت',
  'announce.icon.sparkles': 'لمعة',
  'announce.icon.info': 'معلومة',
  'announce.icon.alert': 'تنبيه',
  'announce.icon.gift': 'هدية',
  'announce.icon.party': 'احتفال',
  'announce.icon.coins': 'أرصدة',
  'announce.icon.rocket': 'صاروخ',
  'announce.icon.calendar': 'تقويم',
  'announce.icon.bell': 'جرس',
  'announce.icon.wrench': 'صيانة',
  'announce.icon.shield': 'أمان',

  'announce.admin.title': 'الإعلانات',
  'announce.admin.subtitle': 'انشر إشعارات تظهر في الشريط الجانبي لجميع المستخدمين. أضف صورة وأيقونة ورابطًا وجدولًا زمنيًا.',
  'announce.admin.new': 'إعلان جديد',
  'announce.admin.loading': 'جارٍ تحميل الإعلانات…',
  'announce.admin.loadFailed': 'تعذّر تحميل الإعلانات.',
  'announce.admin.none': 'لا توجد إعلانات بعد. أنشئ إعلانًا ليظهر في الشريط الجانبي لكل مستخدم.',
  'announce.admin.noneInFilter': 'لا يوجد شيء هنا.',
  'announce.admin.filter': 'تصفية الإعلانات',

  'announce.status.all': 'الكل',
  'announce.status.live': 'منشور الآن',
  'announce.status.scheduled': 'مجدول',
  'announce.status.ended': 'منتهٍ',
  'announce.status.draft': 'مسودة',

  'announce.admin.window': '{from} ← {to}',
  'announce.admin.now': 'الآن',
  'announce.admin.noEnd': 'بلا تاريخ انتهاء',
  'announce.admin.dismissedOne': 'أغلقه مستخدم واحد',
  'announce.admin.dismissedMany': 'أغلقه {n} مستخدم',
  'announce.admin.notDismissible': 'لا يمكن إغلاقه',
  'announce.admin.createdBy': 'بواسطة {name}',
  'announce.admin.edit': 'تعديل {title}',
  'announce.admin.duplicate': 'نسخ {title}',
  'announce.admin.publish': 'نشر {title}',
  'announce.admin.unpublish': 'إلغاء نشر {title}',
  'announce.admin.delete': 'حذف {title}',
  'announce.admin.copySuffix': '(نسخة)',

  'announce.admin.createTitle': 'إعلان جديد',
  'announce.admin.editTitle': 'تعديل الإعلان',
  'announce.admin.sectionContent': 'المحتوى',
  'announce.admin.sectionLook': 'المظهر',
  'announce.admin.sectionLink': 'زر الرابط',
  'announce.admin.sectionSchedule': 'الجدولة',
  'announce.admin.sectionOptions': 'الخيارات',
  'announce.admin.titleEn': 'العنوان (بالإنجليزية)',
  'announce.admin.titleAr': 'العنوان (بالعربية)',
  'announce.admin.bodyEn': 'الرسالة (بالإنجليزية)',
  'announce.admin.bodyAr': 'الرسالة (بالعربية)',
  'announce.admin.arOptional': 'اختياري. يرى مستخدمو العربية النص الإنجليزي إذا تُرك فارغًا.',
  'announce.admin.chars': '{n} / {max}',
  'announce.admin.icon': 'الأيقونة',
  'announce.admin.tone': 'النمط',
  'announce.admin.image': 'الصورة',
  'announce.admin.imageAdd': 'إضافة صورة',
  'announce.admin.imageReplace': 'استبدال',
  'announce.admin.imageRemove': 'إزالة الصورة',
  'announce.admin.imageHint': 'PNG أو JPEG أو WebP أو GIF. الصور العريضة أفضل؛ يتم تغيير حجمها تلقائيًا.',
  'announce.admin.imageProcessing': 'جارٍ تجهيز الصورة…',
  'announce.admin.imageInvalid': 'هذا الملف ليس صورة صالحة.',
  'announce.admin.imageTooBig': 'الصورة كبيرة جدًا (الحد الأقصى 10 ميغابايت).',
  'announce.admin.linkUrl': 'الرابط',
  'announce.admin.linkUrlHint': 'https://… أو صفحة في التطبيق مثل /tasks. اتركه فارغًا لإخفاء الزر.',
  'announce.admin.linkLabel': 'نص الزر (بالإنجليزية)',
  'announce.admin.linkLabelAr': 'نص الزر (بالعربية)',
  'announce.admin.startsAt': 'يبدأ',
  'announce.admin.endsAt': 'ينتهي',
  'announce.admin.startsHint': 'فارغ = فورًا.',
  'announce.admin.endsHint': 'فارغ = حتى تلغي نشره.',
  'announce.admin.presetMonth': 'حتى نهاية الشهر',
  'announce.admin.presetWeek': 'لمدة أسبوع',
  'announce.admin.presetNone': 'بلا تاريخ انتهاء',
  'announce.admin.published': 'منشور',
  'announce.admin.publishedHint': 'عند الإيقاف يُحفظ كمسودة لا يراها أحد.',
  'announce.admin.dismissible': 'يمكن للمستخدمين إغلاقه',
  'announce.admin.dismissibleHint': 'أوقفه للإشعارات التي يجب أن تبقى ظاهرة للجميع.',
  'announce.admin.reset': 'إظهاره مجددًا لكل من أغلقه',
  'announce.admin.resetHint': 'أغلق {n} مستخدم هذا الإعلان.',
  'announce.admin.preview': 'معاينة مباشرة',
  'announce.admin.previewHint': 'هكذا يظهر في الشريط الجانبي.',
  'announce.admin.previewTitle': 'العنوان',
  'announce.admin.previewBody': 'تظهر رسالتك هنا.',
  'announce.admin.create': 'نشر الإعلان',
  'announce.admin.saveDraft': 'حفظ كمسودة',
  'announce.admin.update': 'حفظ التغييرات',
  'announce.admin.createdToast': 'تم حفظ «{title}».',
  'announce.admin.updatedToast': 'تم تحديث «{title}».',
  'announce.admin.deletedToast': 'تم حذف «{title}».',
  'announce.admin.publishedToast': 'تم نشر «{title}».',
  'announce.admin.unpublishedToast': 'أصبح «{title}» مسودة.',
  'announce.admin.saveFailed': 'تعذّر حفظ الإعلان.',
  'announce.admin.deleteFailed': 'تعذّر حذف الإعلان.',
  'announce.admin.deleteTitle': 'حذف «{title}»؟',
  'announce.admin.deleteMessage': 'سيختفي من كل الأشرطة الجانبية. لا يمكن التراجع عن ذلك.',
  'announce.admin.deleteConfirm': 'حذف',
  'announce.admin.endBeforeStart': 'يجب أن يكون تاريخ الانتهاء بعد تاريخ البدء.',
};

const announcements = { en, ar };
export default announcements;
